import {ChevronDownIcon} from "@heroicons/react/20/solid";
import {useState} from "react";
import type {TicketCostAllocation, TicketCostLine} from "./ticket-cost-types";
import {formatTicketCostCurrency, formatTicketCostPercent, formatTicketCostRate, sortTicketCostLines} from "./ticket-cost-types";

export type WellCostSplitListProps = {
  allocations: TicketCostAllocation[];
  lines?: TicketCostLine[];
  currency?: string;
  onViewAllocation?: () => void;
};

/** Compact allocation result for cost context; detailed production math belongs in the allocation inspector. */
export const WellCostSplitList = ({allocations, lines = [], currency = "USD", onViewAllocation}: WellCostSplitListProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const sortedLines = sortTicketCostLines(lines);
  const sortedAllocations = [...allocations].sort((left, right) =>
    right.amount - left.amount
    || right.allocation - left.allocation
    || left.wellName.localeCompare(right.wellName, undefined, {sensitivity: "base"})
    || left.id.localeCompare(right.id));

  return (
    <div>
      <div className="divide-y divide-[var(--ots-outline-variant)]">
        {sortedAllocations.map((allocation) => {
          const isExpanded = expanded.has(allocation.id);
          const wellLines = sortedLines.flatMap((line) => {
            const allocatedLine = line.wellAllocations?.find((candidate) => candidate.wellId === allocation.id);
            if (allocatedLine) return [{line, allocation: allocatedLine.allocation, amount: allocatedLine.amount}];

            const directlyOwned = line.wellId === allocation.id
              || (!line.wellId && line.wellName === allocation.wellName);
            return directlyOwned
              ? [{line, allocation: line.allocation ?? allocation.allocation, amount: line.amount}]
              : [];
          });
          const lineCount = lines.length ? wellLines.length : allocation.lineCount;
          return (
            <div key={allocation.id}>
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Hide" : "Show"} ${allocation.wellName} charge lines`}
                onClick={() => toggle(allocation.id)}
                className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 py-2.5 text-left text-xs hover:bg-[var(--ots-background)] focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--ots-primary)]">
                <ChevronDownIcon className={["size-4 text-[var(--ots-text-muted)] transition-transform", isExpanded ? "rotate-180" : ""].join(" ")}/>
                <span className="min-w-0">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate font-semibold text-[var(--ots-text)]" title={allocation.wellName}>{allocation.wellName}</span>
                    {allocation.afe && <span className="shrink-0 text-[10px] text-[var(--ots-text-muted)]">AFE {allocation.afe}</span>}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-[var(--ots-text-muted)]">
                    {formatTicketCostPercent(allocation.allocation)} · {lineCount} charge line{lineCount === 1 ? "" : "s"}
                  </span>
                  <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-[var(--ots-surface-variant)]">
                    <span
                      className="block h-full rounded-full bg-[var(--ots-primary)]"
                      style={{width: `${Math.max(allocation.allocation * 100, allocation.allocation > 0 ? 1 : 0)}%`}}/>
                  </span>
                </span>
                <strong className="min-w-20 text-right tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(allocation.amount, currency)}</strong>
              </button>

              {isExpanded && (
                <div className="mb-2 ml-6 overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-background)]">
                  {wellLines.length > 0 && (
                    <div className="grid grid-cols-[minmax(0,1fr)_5.5rem_4.5rem_5.5rem] gap-3 border-b border-[var(--ots-outline-variant)] px-3 py-1 text-[9px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
                      <span>Charge</span>
                      <span className="text-right">Rate</span>
                      <span className="text-right">Split</span>
                      <span className="text-right">Amount</span>
                    </div>
                  )}
                  {wellLines.map(({line, allocation: lineAllocation, amount}) => (
                    <div key={line.id} className="grid grid-cols-[minmax(0,1fr)_5.5rem_4.5rem_5.5rem] items-baseline gap-3 border-b border-[var(--ots-outline-variant)] px-3 py-2 text-[11px] last:border-b-0">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-[var(--ots-text)]" title={line.label}>{line.label}</span>
                        <span className="block truncate text-[10px] text-[var(--ots-text-muted)]">{line.component?.label ?? "Unclassified component"}</span>
                      </span>
                      <span className="text-right tabular-nums text-[var(--ots-text)]">{formatTicketCostRate(line.rate, currency, line.rateUnit)}</span>
                      <span className="text-right tabular-nums text-[var(--ots-text-muted)]">{formatTicketCostPercent(lineAllocation)}</span>
                      <strong className="text-right tabular-nums text-[var(--ots-text)]">{formatTicketCostCurrency(amount, currency)}</strong>
                    </div>
                  ))}
                  {!wellLines.length && <p className="px-3 py-2 text-[11px] text-[var(--ots-text-muted)]">No charges were calculated specifically for this well.</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {onViewAllocation && (
        <button type="button" onClick={onViewAllocation} className="mt-3 text-xs font-semibold text-[var(--ots-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]">
          View allocation calculation
        </button>
      )}
    </div>
  );
};
